import { GlobalService } from "@sdk-point/talisis";

const GlobalServiceSDK = new GlobalService(
  process.env.REACT_APP_ENV,
  process.env.REACT_APP_X_API_KEY
);

/** Cache de prerrequisitos; se carga de forma lazy en la primera llamada a blockers. */
let coursePrerequisitesCache = null;

const LOG_PREFIX = "[classValidator]";

/**
 * Parsea configValue devuelto por getConfig.
 * El valor puede ser JSON o un objeto JS con claves numéricas sin comillas (ej. "37467: {...}").
 * @param {string} configValue - String con la configuración (JSON o objeto literal)
 * @returns {Object<number, {required: number[], reason: string}>}
 */
function parseCoursePrerequisitesConfig(configValue) {
  // Si ya es un objeto (ej. respuesta directa), devolverlo
  if (typeof configValue !== "string") {
    const result =
      typeof configValue === "object" && configValue !== null
        ? configValue
        : {};
    return result;
  }

  const trimmed = configValue.trim();
  if (!trimmed) {
    return {};
  }

  // Intentar parsear como JSON directo
  try {
    const parsed = JSON.parse(trimmed);
    return parsed;
  } catch (firstErr) {
    console.error(
      `${LOG_PREFIX} parseCoursePrerequisitesConfig JSON.parse falló, intentando formato objeto literal`,
      firstErr?.message
    );
  }

  // 2) Intentar convertir objeto literal a JSON (claves sin comillas → con comillas)
  try {
    const asJson = trimmed
      .replace(/\brequired\s*:/g, '"required":')
      .replace(/\breason\s*:/g, '"reason":')
      .replace(/(\d+):/g, '"$1":');
    const parsed = JSON.parse(asJson);
    return parsed;
  } catch (secondErr) {
    console.error(
      `${LOG_PREFIX} parseCoursePrerequisitesConfig regex→JSON falló`,
      secondErr?.message
    );
  }

  // 3) Fallback: evaluar como objeto literal de JavaScript (getConfig devuelve este formato)
  // Solo se usa con datos del API interno; no es input de usuario.
  try {
    const fn = new Function(`return (${trimmed})`);
    const parsed = fn();
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (fnErr) {
    console.error(
      `${LOG_PREFIX} parseCoursePrerequisitesConfig Function fallback falló`,
      fnErr
    );
  }

  console.error(
    `${LOG_PREFIX} parseCoursePrerequisitesConfig todos los intentos fallaron → {}`
  );
  return {};
}

/**
 * Obtiene la configuración de prerrequisitos (cacheada tras la primera carga).
 * getConfig devuelve { configKey, configValue }; el mapa de prerrequisitos está en configValue.
 * @returns {Promise<Object<number, {required: number[], reason: string}>>}
 */
async function getCoursePrerequisites() {
  if (coursePrerequisitesCache === null) {
    try {
      const response = await GlobalServiceSDK.getConfig(
        `course_prerequisites`
      );

      const raw =
        response && typeof response.configValue !== "undefined"
          ? response.configValue
          : response;
      coursePrerequisitesCache = parseCoursePrerequisitesConfig(raw);
    } catch (err) {
      coursePrerequisitesCache = {};
    }
  }
  return coursePrerequisitesCache;
}

/**
 * Configuración de prerrequisitos para cursos específicos.
 * Define qué cursos deben estar aprobados antes de poder cursar otros.
 *
 * @type {Object<number, {required: number[], reason: string}>}
 * @property {number[]} required - Array de IDs de cursos que deben estar aprobados
 * @property {string} reason - Mensaje explicativo del bloqueo cuando no se cumplen los prerrequisitos
 */
/**
 * Clase utilitaria para validar y bloquear cursos según sus prerrequisitos.
 * Proporciona métodos estáticos para aplicar reglas de bloqueo a programas académicos.
 */
export class CoursesValidators {
  /**
   * Valida y marca cursos como bloqueados si no se han cumplido sus prerrequisitos.
   * Recorre todos los términos del programa y verifica cada curso contra los prerrequisitos configurados.
   *
   * @param {Array<{courses?: Array<{course_id: number, approved: boolean}>}>} program - Estructura del programa académico por términos/cuatrimestres
   * @returns {Promise<Array<{courses: Array<{blocked: boolean, blockedReason: string|null, blockedByCourseName: string|null, missingPrerequisites: Array}>}>}>} Programa con cursos marcados como bloqueados cuando corresponda
   */
  static async blockers(program) {

    // Si no hay programa o está vacío, devolver tal cual (no hay nada que validar)
    if (!Array.isArray(program) || program.length === 0) {
      return program;
    }

    // 1) Cargar configuración de prerrequisitos (curso_id → { required: [ids], reason })
    const coursePrerequisites = await getCoursePrerequisites();
    const configKeys =
      coursePrerequisites && typeof coursePrerequisites === "object"
        ? Object.keys(coursePrerequisites)
        : [];

    // 2) Mapa curso_id → curso para resolver nombres de prerrequisitos
    const coursesMap = new Map();
    program.forEach((term) => {
      (term.courses || []).forEach((course) => {
        coursesMap.set(course.course_id, course);
      });
    });

    // 3) IDs de cursos que el usuario ya tiene aprobados (progress_percentage === 100 Y approved === true)
    const approvedCourseIds = new Set(
      program
        .flatMap((term) => term.courses || [])
        .filter((course) => course.progress_percentage === 100 && course.approved === true)
        .map((course) => Number(course.course_id))
    );

    // 4) Por cada término y cada curso: ver si tiene prerrequisitos y si los cumple
    const result = program.map((term, termIndex) => {
      const coursesResult = (term.courses || []).map((course, courseIndex) => {
        const courseId = Number(course.course_id);
        const config =
          coursePrerequisites[courseId] ??
          coursePrerequisites[String(courseId)];

        // Sin regla de prerrequisitos → no bloqueado
        if (!config) {
          return {
            ...course,
            blocked: false,
            blockedReason: null,
            blockedByCourseName: null,
            missingPrerequisites: [],
          };
        }

        // Qué IDs requiere este curso y cuáles de esos NO están aprobados
        const requiredIds = Array.isArray(config.required)
          ? config.required
          : [];
        const missing = requiredIds.filter(
          (id) => !approvedCourseIds.has(Number(id))
        );

        const missingPrerequisites = missing.map((id) => {
          const prerequisiteCourse =
            coursesMap.get(id) ?? coursesMap.get(Number(id));
          return {
            course_id: id,
            name: prerequisiteCourse?.name || "Curso no encontrado",
            approved: prerequisiteCourse?.approved || false,
            tagLabel: prerequisiteCourse?.tagLabel || null,
            is_estadia_empresarial:
              prerequisiteCourse?.is_estadia_empresarial || false,
          };
        });

        const blockedByCourseName =
          missingPrerequisites.length > 0 ? missingPrerequisites[0].name : null;

        return {
          ...course,
          blocked: missing.length > 0,
          blockedReason: missing.length > 0 ? config.reason : null,
          blockedByCourseName,
          missingPrerequisites,
        };
      });

      return { ...term, courses: coursesResult };
    });

    return result;
  }
}
